// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stack,
  Alert,
  Divider,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  CircularProgress
} from '@mui/material';
import {
  Save,
  Cancel,
  Add,
  Warning,
  Refresh
} from '@mui/icons-material';
import { apiClient, API_ENDPOINTS } from '../config/api';
import CategoryEditor from './CategoryEditor';
import QualificationItemsStep from './wizard/QualificationItemsStep';
import AlertDialog from './common/AlertDialog';

/**
 * QACriteriaEdit Component
 * 
 * Main edit form for QA Criteria:
 * - Edit metadata (name, team, department, description, optional Knowledge)
 * - Edit status flags (is_active, is_default)
 * - Edit categories and metrics
 * - Add/Remove categories
 * - Real-time validation
 * - Save/Cancel with confirmation
 */
const QACriteriaEdit = ({ criteria, onSave, onCancel }) => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    team_name: '',
    department: '',
    description: '',
    is_active: true,
    is_default: false,
    team_id: '',
    visibility: 'team',
    criteria_type: 'matrix',
    // QA Context fields
    conversation_type: '',
    participants: '',
    conversation_objective: '',
    ideal_approach: '',
    context_notes: '',
    target_audience: '',
    categories: []
  });

  const [saving, setSaving] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', severity: 'info' });
  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  
  // Team state
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  
  // QA Context state
  const [contextSettings, setContextSettings] = useState({
    conversation_types: [],
    participant_types: [],
    target_audiences: []
  });
  const [contextPreview, setContextPreview] = useState('');
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [previewGenerated, setPreviewGenerated] = useState(false);  // Track if preview was generated
  const [formChanged, setFormChanged] = useState(false);            // Track if form was edited after preview

  // Fetch teams for filtering (only user's teams)
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoadingTeams(true);
        const response = await apiClient.get(API_ENDPOINTS.TEAMS_FOR_FILTERING);
        setTeams(response.data.teams || []); // Extract teams array from response
      } catch (error) {
        console.error('Failed to fetch teams for filtering:', error);
      } finally {
        setLoadingTeams(false);
      }
    };
    
    fetchTeams();
  }, []);

  // Fetch QA Context dropdown options
  useEffect(() => {
    const fetchContextSettings = async () => {
      try {
        console.log('🔍 Fetching QA context settings from:', API_ENDPOINTS.SETTINGS_CATEGORY_QA_CONTEXT);
        // Fetch all QA context settings from category
        const response = await apiClient.get(API_ENDPOINTS.SETTINGS_CATEGORY_QA_CONTEXT);
        
        console.log('🔍 QA context settings response:', response.data);
        
        // Extract setting_value arrays from each setting
        const settings = response.data || [];
        const convTypes = settings.find(s => s.setting_key === 'conversation_types')?.setting_value || [];
        const partTypes = settings.find(s => s.setting_key === 'participant_types')?.setting_value || [];
        const audiences = settings.find(s => s.setting_key === 'target_audiences')?.setting_value || [];
        
        console.log('🔍 Extracted context settings:', {
          conversation_types: convTypes.length,
          participant_types: partTypes.length,
          target_audiences: audiences.length
        });
        
        console.log('🔍 First conversation type:', convTypes[0]);
        console.log('🔍 First participant type:', partTypes[0]);
        console.log('🔍 First target audience:', audiences[0]);
        
        setContextSettings({
          conversation_types: convTypes,
          participant_types: partTypes,
          target_audiences: audiences
        });
      } catch (error) {
        console.error('Failed to fetch context settings:', error);
      }
    };
    
    fetchContextSettings();
  }, []);

  // Initialize form with criteria data
  useEffect(() => {
    if (criteria) {
      console.log('🔍 Loading criteria data for edit:', criteria);
      console.log('🔍 QA Context fields:', {
        conversation_type: criteria.conversation_type,
        participants: criteria.participants,
        conversation_objective: criteria.conversation_objective,
        ideal_approach: criteria.ideal_approach,
        context_notes: criteria.context_notes,
        target_audience: criteria.target_audience
      });
      
      const mappedCats = (criteria.categories || []).map((c) => ({
        ...c,
        use_knowledge_for_analysis: false
      }));

      const newFormData = {
        name: criteria.name || '',
        team_name: criteria.team_name || '',
        department: criteria.department || '',
        description: criteria.description || '',
        is_active: criteria.is_active !== false,
        is_default: criteria.is_default || false,
        team_id: criteria.team_id || '',
        visibility: criteria.visibility || 'team',
        criteria_type: criteria.criteria_type || 'matrix',
        // QA Context
        conversation_type: criteria.conversation_type || '',
        participants: criteria.participants || '',
        conversation_objective: criteria.conversation_objective || '',
        ideal_approach: criteria.ideal_approach || '',
        context_notes: criteria.context_notes || '',
        target_audience: criteria.target_audience || '',
        categories: mappedCats
      };

      console.log('🔍 Setting form data:', newFormData);
      setFormData(newFormData);
      
      // If criteria has high_level_context, load it and mark as generated
      if (criteria.high_level_context) {
        setContextPreview(criteria.high_level_context);
        setPreviewGenerated(true);
        setFormChanged(false);
      } else {
        // No preview exists, reset state
        setContextPreview('');
        setPreviewGenerated(false);
        setFormChanged(false);
      }
    }
  }, [criteria]);
  
  // Generate context preview (used by both manual button and before save when needed)
  const generateContextPreview = async () => {
    const response = await apiClient.post(API_ENDPOINTS.QA_CONTEXT_GENERATE_PREVIEW, {
      conversation_type: formData.conversation_type,
      participants: formData.participants,
      conversation_objective: formData.conversation_objective,
      ideal_approach: formData.ideal_approach,
      context_notes: formData.context_notes,
      target_audience: formData.target_audience,
      categories: formData.categories,
      criteria_type: formData.criteria_type || 'matrix'
    });
    if (response.data.success) {
      setContextPreview(response.data.preview);
      setPreviewGenerated(true);
      setFormChanged(false);
      return response.data.preview;
    }
    return null;
  };

  const handleGenerateContext = async () => {
    setGeneratingPreview(true);
    setContextPreview('');
    try {
      const preview = await generateContextPreview();
      if (!preview) {
        setContextPreview('Error: Unable to generate preview. Please try again.');
        setPreviewGenerated(false);
      }
    } catch (error) {
      console.error('Failed to generate preview:', error);
      setContextPreview('Error: Unable to generate preview. Please try again.');
      setPreviewGenerated(false);
    } finally {
      setGeneratingPreview(false);
    }
  };

  // Handle field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Only invalidate preview if QA Context Settings changed
    // Metadata changes (name, team_id, visibility, status, description) don't require preview regeneration
    const metadataFields = ['name', 'team_id', 'visibility', 'is_active', 'is_default', 'team_name', 'department', 'description'];
    const isMetadataChange = metadataFields.includes(field);
    
    if (!isMetadataChange) {
      // QA Context Settings changed - invalidate preview
      setFormChanged(true);
      setPreviewGenerated(false);
    }
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle category changes
  const handleCategoryChange = (index, updatedCategory) => {
    const newCategories = [...formData.categories];
    newCategories[index] = updatedCategory;
    setFormData(prev => ({ ...prev, categories: newCategories }));
    setFormChanged(true);        // Mark form as changed
    setPreviewGenerated(false);  // Invalidate preview
  };

  // Add new category
  const handleAddCategory = () => {
    const newCategory = {
      category_name: '',
      category_key: `category-${Date.now()}`,
      description: '',
      weight_percentage: 0,
      display_order: formData.categories.length,
      color_code: null,
      metrics: [],
      use_knowledge_for_analysis: false
    };
    setFormData(prev => ({
      ...prev,
      categories: [...prev.categories, newCategory]
    }));
    setFormChanged(true);        // Mark form as changed
    setPreviewGenerated(false);  // Invalidate preview
  };

  // Delete category
  const handleDeleteCategory = (index) => {
    const newCategories = formData.categories.filter((_, idx) => idx !== index);
    setFormData(prev => ({ ...prev, categories: newCategories }));
    setFormChanged(true);        // Mark form as changed
    setPreviewGenerated(false);  // Invalidate preview
  };

  // Update qualification items (single category metrics) for boolean criteria
  const handleQualificationItemsChange = (newCategories) => {
    setFormData(prev => ({ ...prev, categories: newCategories }));
    setFormChanged(true);
    setPreviewGenerated(false);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Validate basic fields
    if (!formData.name || formData.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }
    if (!formData.team_id || formData.team_id.trim().length === 0) {
      newErrors.team_id = 'Team is required';
    }

    // Validate categories or qualification items
    const isBooleanCriteria = formData.criteria_type === 'boolean';
    if (isBooleanCriteria) {
      const qualMetrics = formData.categories?.[0]?.metrics || [];
      if (qualMetrics.length === 0) {
        newErrors.categories = 'At least one qualification item is required';
      } else {
        qualMetrics.forEach((m, i) => {
          if (!m.description || !String(m.description).trim()) {
            if (!newErrors.qualificationItemErrors) newErrors.qualificationItemErrors = {};
            newErrors.qualificationItemErrors[i] = 'What to qualify (description) is required';
          }
        });
      }
    } else {
      const isBooleanCriteria = formData.criteria_type === 'boolean';
      if (formData.categories.length === 0) {
        newErrors.categories = 'At least one category is required';
      }

      if (!isBooleanCriteria) {
        const totalCategoryWeight = formData.categories.reduce(
          (sum, cat) => sum + (cat.weight_percentage || 0),
          0
        );
        if (totalCategoryWeight !== 100) {
          newErrors.categories = `Total category weights must equal 100% (currently ${totalCategoryWeight}%)`;
        }
      }

      // Validate each category
      const categoryErrors = {};
      formData.categories.forEach((category, catIndex) => {
        const catErrors = {};

        if (!category.category_name || category.category_name.trim().length < 3) {
          catErrors.category_name = 'Category name must be at least 3 characters';
        }

        if (!isBooleanCriteria && (category.weight_percentage < 0 || category.weight_percentage > 100)) {
          catErrors.weight_percentage = 'Weight must be between 0 and 100';
        }

        // Validate metrics
        if (!category.metrics || category.metrics.length === 0) {
          catErrors.metrics = 'At least one metric is required per category';
        }

      if (!isBooleanCriteria) {
        const totalMetricWeight = (category.metrics || []).reduce(
          (sum, metric) => sum + (metric.weight_percentage || 0),
          0
        );
        if (totalMetricWeight !== category.weight_percentage) {
          catErrors.metrics = catErrors.metrics || {};
          if (typeof catErrors.metrics === 'string') {
            catErrors.metrics = {};
          }
          catErrors.metricsWeightError = `Metric weights must equal category weight ${category.weight_percentage}% (currently ${totalMetricWeight}%)`;
        }
      }

      // Validate each metric
      (category.metrics || []).forEach((metric, metricIndex) => {
        const metricErrors = {};

        if (!metric.metric_name || metric.metric_name.trim().length < 3) {
          metricErrors.metric_name = 'Metric name must be at least 3 characters';
        }

        if (!isBooleanCriteria && (metric.weight_percentage < 0 || metric.weight_percentage > 100)) {
          metricErrors.weight_percentage = 'Weight must be between 0 and 100';
        }

        if (Object.keys(metricErrors).length > 0) {
          if (!catErrors.metrics) catErrors.metrics = {};
          catErrors.metrics[metricIndex] = metricErrors;
        }
      });

      if (Object.keys(catErrors).length > 0) {
        categoryErrors[catIndex] = catErrors;
      }
    });

      if (Object.keys(categoryErrors).length > 0) {
        newErrors.categoryErrors = categoryErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Convert FastAPI/Pydantic error detail (string or array of { msg, loc }) to a single string
  const formatApiErrorDetail = (detail) => {
    if (detail == null) return null;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => (d && typeof d.msg === 'string' ? d.msg : JSON.stringify(d))).join('. ') || null;
    }
    return typeof detail === 'object' ? JSON.stringify(detail) : String(detail);
  };

  // Handle save
  const handleSave = async () => {
    // Validate form first
    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    const hasQAContextChanges = criteria && (
      formData.conversation_type !== criteria.conversation_type ||
      formData.participants !== criteria.participants ||
      formData.conversation_objective !== criteria.conversation_objective ||
      formData.ideal_approach !== criteria.ideal_approach ||
      formData.context_notes !== criteria.context_notes ||
      formData.target_audience !== criteria.target_audience
    );

    const hasCategoryChanges = criteria && (
      JSON.stringify(formData.categories) !== JSON.stringify(criteria.categories)
    );

    let previewForSave = null;
    const needPreview = (hasQAContextChanges || hasCategoryChanges) && (!previewGenerated || formChanged);
    if (needPreview) {
      setGeneratingPreview(true);
      try {
        previewForSave = await generateContextPreview();
        setGeneratingPreview(false);
        if (!previewForSave) {
          setAlertDialog({
            open: true,
            title: 'Preview Failed',
            message: 'Could not generate context preview. Please try again or click "Generate Context Preview" manually.',
            severity: 'error'
          });
          return;
        }
      } catch (err) {
        setGeneratingPreview(false);
        setAlertDialog({
          open: true,
          title: 'Preview Failed',
          message: 'Failed to generate context preview. Please try again.',
          severity: 'error'
        });
        return;
      }
    }

    setSaving(true);

    try {
      const highLevelContext = previewForSave != null ? previewForSave : contextPreview;
      const updateData = {
        name: formData.name,
        team_id: formData.team_id,
        visibility: formData.visibility,
        team_name: formData.team_name || null,
        department: formData.department || null,
        description: formData.description || null,
        is_active: formData.is_active,
        is_default: formData.is_default,
        criteria_type: formData.criteria_type || 'matrix',
        // QA Context fields
        conversation_type: formData.conversation_type || null,
        participants: formData.participants || null,
        conversation_objective: formData.conversation_objective || null,
        ideal_approach: formData.ideal_approach || null,
        context_notes: formData.context_notes || null,
        target_audience: formData.target_audience || null,
        high_level_context: highLevelContext || null,
        knowledge_item_ids: null,
        categories: formData.categories.map((category, catIndex) => ({
          category_name: category.category_name,
          category_key: category.category_key || `category-${catIndex}`,
          description: category.description || null,
          weight_percentage: category.weight_percentage,
          display_order: catIndex,
          color_code: category.color_code || null,
          use_knowledge_for_analysis: false,
          metrics: category.metrics.map((metric, metricIndex) => {
            const isBoolean = formData.criteria_type === 'boolean';
            return {
              metric_name: metric.metric_name,
              metric_key: metric.metric_key || `metric-${catIndex}-${metricIndex}`,
              description: metric.description || null,
              weight_percentage: metric.weight_percentage,
              possible_scores: metric.possible_scores || null,
              evaluation_guide: metric.evaluation_guide || null,
              keywords: metric.keywords || null,
              ...(isBoolean && { is_mandatory: metric.is_mandatory ?? true }),
              display_order: metricIndex
            };
          })
        }))
      };

      // Call API
      const response = await apiClient.put(
        `${API_ENDPOINTS.QA_CRITERIA_UPDATE}/${criteria.id}`,
        updateData
      );

      if (response.data.success) {
        onSave(response.data.criteria);
      } else {
        const detail = response.data.detail;
        setErrors({ save: formatApiErrorDetail(detail) || 'Failed to save criteria' });
      }
    } catch (error) {
      console.error('Error saving criteria:', error);
      const detail = error.response?.data?.detail;
      setErrors({
        save: formatApiErrorDetail(detail) || 'Failed to save criteria. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setShowCancelConfirm(true);
  };

  const confirmCancel = () => {
    setShowCancelConfirm(false);
    onCancel();
  };

  // Calculate total category weight
  const totalCategoryWeight = formData.categories.reduce(
    (sum, cat) => sum + (cat.weight_percentage || 0),
    0
  );

  const isBoolean = formData.criteria_type === 'boolean';
  const categoryWeightError = isBoolean ? false : totalCategoryWeight !== 100;

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Edit QA Criteria
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Edit criteria metadata, categories, and metrics. All changes must be saved to take effect.
          </Typography>
        </CardContent>
      </Card>

      {/* Save Error */}
      {errors.save && (
        <Alert severity="error" onClose={() => setErrors({ ...errors, save: null })} sx={{ mb: 3 }}>
          {typeof errors.save === 'string' ? errors.save : formatApiErrorDetail(errors.save)}
        </Alert>
      )}

      {/* Metadata Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Criteria Metadata
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            {/* Name */}
            <TextField
              label="Criteria Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name || 'Unique name for this criteria'}
            />

            {/* Team Selection */}
            <FormControl fullWidth required error={!!errors.team_id}>
              <InputLabel>Team</InputLabel>
              <Select
                value={formData.team_id}
                label="Team"
                onChange={(e) => handleChange('team_id', e.target.value)}
                disabled={loadingTeams}
              >
                {loadingTeams ? (
                  <MenuItem disabled>Loading teams...</MenuItem>
                ) : teams.length === 0 ? (
                  <MenuItem disabled>No teams available</MenuItem>
                ) : (
                  teams.filter(t => t.is_active).map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name} ({team.code})
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.team_id && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {errors.team_id}
                </Typography>
              )}
            </FormControl>
            
            {/* Visibility */}
            <FormControl fullWidth>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={formData.visibility}
                label="Visibility"
                onChange={(e) => handleChange('visibility', e.target.value)}
              >
                <MenuItem value="private">
                  🔒 Private - Only you can access
                </MenuItem>
                <MenuItem value="team">
                  👥 Team - All team members can access
                </MenuItem>
                <MenuItem value="public">
                  🌐 Public - Everyone can access
                </MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 2 }}>
                {formData.visibility === 'private' && 'Only you can view and edit this QA criteria'}
                {formData.visibility === 'team' && 'All team members can view, team leaders can edit'}
                {formData.visibility === 'public' && 'Everyone can view, only you and admins can edit'}
              </Typography>
            </FormControl>

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              multiline
              minRows={2}
              placeholder="Brief description of this criteria"
            />

            {/* Status Switches */}
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_default}
                    onChange={(e) => handleChange('is_default', e.target.checked)}
                  />
                }
                label="Set as Default"
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* QA Context Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            QA Context Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define the conversation context to help AI provide more accurate analysis
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            {/* Conversation Type */}
            <FormControl fullWidth>
              <InputLabel>Conversation Type</InputLabel>
              <Select
                value={formData.conversation_type}
                label="Conversation Type"
                onChange={(e) => handleChange('conversation_type', e.target.value)}
              >
                {console.log('🔍 Conversation Type Select - value:', formData.conversation_type, 'options:', contextSettings.conversation_types)}
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {contextSettings.conversation_types.map((item, idx) => (
                  <MenuItem key={idx} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Participant Type */}
            <FormControl fullWidth>
              <InputLabel>Participant Type</InputLabel>
              <Select
                value={formData.participants}
                label="Participant Type"
                onChange={(e) => handleChange('participants', e.target.value)}
              >
                {console.log('🔍 Participant Type Select - value:', formData.participants, 'options:', contextSettings.participant_types)}
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {contextSettings.participant_types.map((item, idx) => (
                  <MenuItem key={idx} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Target Audience */}
            <FormControl fullWidth>
              <InputLabel>Target Audience</InputLabel>
              <Select
                value={formData.target_audience}
                label="Target Audience"
                onChange={(e) => handleChange('target_audience', e.target.value)}
              >
                {console.log('🔍 Target Audience Select - value:', formData.target_audience, 'options:', contextSettings.target_audiences)}
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {contextSettings.target_audiences.map((item, idx) => (
                  <MenuItem key={idx} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Conversation Objective */}
            <TextField
              label="Conversation Objective"
              value={formData.conversation_objective}
              onChange={(e) => handleChange('conversation_objective', e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="What should this conversation achieve?"
            />

            {/* Ideal Approach */}
            <TextField
              label="Ideal Approach"
              value={formData.ideal_approach}
              onChange={(e) => handleChange('ideal_approach', e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="How should the conversation be conducted?"
            />

            {/* Additional Context Notes */}
            <TextField
              label="Additional Context Notes"
              value={formData.context_notes}
              onChange={(e) => handleChange('context_notes', e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Any additional context or special considerations..."
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Categories Section or Qualification Items (boolean) */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {isBoolean ? (
            <QualificationItemsStep
              formData={formData}
              errors={errors}
              onQualificationItemsChange={handleQualificationItemsChange}
            />
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Categories & Metrics
                </Typography>
                <Chip
                  label={`${formData.categories.length} categories`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>

              {/* Category Weight Progress */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Category Weight
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color={categoryWeightError ? 'error' : 'success.main'}
                    fontWeight="medium"
                  >
                    {totalCategoryWeight}% / 100%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(totalCategoryWeight, 100)}
                  color={categoryWeightError ? 'error' : 'success'}
                  sx={{ height: 8, borderRadius: 1 }}
                />
                {categoryWeightError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    <Typography variant="caption">
                      {errors.categories || `Total category weights must equal 100% (currently ${totalCategoryWeight}%)`}
                    </Typography>
                  </Alert>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Categories List */}
              {formData.categories.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No categories defined. Click "Add Category" to create one.
                </Alert>
              ) : (
                <Box sx={{ mb: 2 }}>
                  {formData.categories.map((category, index) => (
                    <CategoryEditor
                      key={index}
                      category={category}
                      index={index}
                      onChange={handleCategoryChange}
                      onDelete={handleDeleteCategory}
                      error={errors.categoryErrors?.[index]}
                      criteriaType={formData.criteria_type || 'matrix'}
                    />
                  ))}
                </Box>
              )}

              {/* Add Category Button */}
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddCategory}
              >
                Add Category
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* QA Context Preview - MOVED TO BOTTOM */}
      <Card sx={{ mb: 3, backgroundColor: '#f5f7fa' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            📄 QA Context Preview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is the EXACT context that will be sent to AI for Sentiment Analysis, QA Scoring, and Improvement Suggestions. Review carefully before saving.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box 
            sx={{ 
              p: 2, 
              backgroundColor: 'white', 
              borderRadius: 1,
              border: '1px solid #e0e0e0',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              minHeight: 200,
              maxHeight: 600,
              overflow: 'auto',
              fontFamily: 'monospace'
            }}
          >
            {generatingPreview ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Generating preview with Ollama...
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {contextPreview || 'Click "Generate Context Preview" button below to see what AI will receive.'}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Status Alerts */}
      {!previewGenerated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Required:</strong> Click "Generate Context Preview" to verify what AI will receive before saving.
        </Alert>
      )}

      {formChanged && previewGenerated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Warning:</strong> Form has been modified. Please re-generate context preview to ensure accuracy before saving.
        </Alert>
      )}

      {previewGenerated && !formChanged && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>Ready to save:</strong> Context preview is up-to-date and matches current form data.
        </Alert>
      )}

      {/* Action Buttons */}
      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={generatingPreview ? <CircularProgress size={16} /> : <Refresh />}
              onClick={handleGenerateContext}
              disabled={generatingPreview}
            >
              {generatingPreview ? 'Generating...' : '🪄 Generate Context Preview'}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving || generatingPreview || categoryWeightError}
            >
              {saving ? 'Saving...' : generatingPreview ? 'Generating preview...' : 'Save Changes'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelConfirm} onClose={() => setShowCancelConfirm(false)}>
        <DialogTitle>Cancel Editing?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel? All unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelConfirm(false)}>
            Continue Editing
          </Button>
          <Button onClick={confirmCancel} color="error" variant="contained">
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ open: false, title: '', message: '', severity: 'info' })}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
      />
    </Box>
  );
};

export default QACriteriaEdit;
